import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { inr } from "@/lib/format";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ApprovalRequestButton } from "@/components/pawkt/ApprovalRequestButton";

/* Server-authoritative eligibility: backend POST /invoices/{id}/cancel allows discard ONLY for
   an unpaid invoice with zero payments (partial/paid -> 409). The UI mirrors that gate for
   discoverability but never decides authority on its own. */
export const canDiscardInvoice = (inv) =>
  !!inv && inv.status === "unpaid" && Number(inv.amount_paid || 0) === 0;

export function DiscardInvoiceModal({ invoice, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/cancel`, { reason: reason.trim(), expected_updated_at: invoice.updated_at });
      toast.success(`Invoice ${invoice.invoice_number} cancelled`); onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-sm" data-testid="discard-invoice-modal">
        <DialogHeader><DialogTitle className="font-display">Cancel invoice · {invoice.invoice_number}?</DialogTitle></DialogHeader>
        <div className="rounded-xl border border-pawkt-border bg-pawkt-bg p-3 text-sm space-y-1" data-testid="discard-context">
          <div className="flex justify-between"><span className="text-slate-500">Customer</span><b className="text-slate-900">{invoice.customer_name}</b></div>
          <div className="flex justify-between"><span className="text-slate-500">Amount</span><b className="text-slate-900 tabular">{inr(invoice.total)}</b></div>
        </div>
        <p className="text-sm text-slate-500">This invoice will <b>not be deleted</b>. It stays in your records marked <b>Cancelled</b> for your audit trail, and reverses its eligible billing side-effects (package units, inventory, coupon) per Pawkt's cancellation contract. This cannot be undone.</p>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Reason <span className="text-rose-500">*</span></Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="discard-reason" placeholder="e.g. Wrong service selected" className="bg-white" rows={3} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="discard-cancel" className="h-11">Keep Invoice</Button>
          <Button onClick={submit} disabled={busy} data-testid="discard-submit" className="h-11 bg-rose-600 hover:bg-rose-700 text-white">{busy ? "Cancelling…" : "Cancel Invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Reusable discard control. Renders nothing unless the user has invoices.edit AND the invoice
   is server-eligible (unpaid, zero paid). Discoverable everywhere an invoice is shown. */
export function DiscardInvoiceButton({ invoice, onDone, size = "sm", className = "", label = "Cancel Invoice" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!canDiscardInvoice(invoice)) return null;
  if (!hasPerm(user, "invoices.edit"))
    return <ApprovalRequestButton action="invoices.cancel" targetId={invoice.id} targetLabel={invoice.invoice_number} size={size} className={className} />;
  return (
    <>
      <Button size={size} variant="outline" data-testid={`discard-btn-${invoice.id}`} onClick={() => setOpen(true)}
        className={`gap-1 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 ${className}`}>
        <Trash2 className="h-3.5 w-3.5" /> {label}
      </Button>
      {open && <DiscardInvoiceModal invoice={invoice} onClose={() => setOpen(false)}
        onDone={() => { setOpen(false); onDone && onDone(); }} />}
    </>
  );
}
